<script lang="ts">
	import { goto } from '$app/navigation';
	import { resolve } from '$app/paths';
	import { page } from '$app/state';
	import { getHessianClientContext } from '$lib/context/hessianClientContext.svelte';
	import { getOrganizationContext } from '$lib/context/organizationContext.svelte';
	import {
		Chip,
		StatusGlyph,
		MetricCard,
		Gauge,
		TokenMeter,
		Timeline,
		type TimelineItem,
		type TimelineTone,
		Tree,
		type TreeNode,
		Scatterplot,
		type ScatterPoint
	} from '@hessianhq/ui';
	import type { Status } from '@hessianhq/ui';
	import type { components } from '$api/schema';
	import { entityUrl } from '../audit/entity-url';
	import { onMount } from 'svelte';
	import { toast } from '$lib/utils/toast.svelte';
	import type { PageProps } from './$types';

	// `disableHeaderTransition` opts the page header out of the
	// cross-page `view-transition-name: header` group. Used by the public
	// splash showcase (which embeds this component as a static preview)
	// so navigating from `/` to `/dashboard` doesn't morph the splash's
	// preview title into the real dashboard title — they're visually
	// similar enough that the morph reads like a glitch.
	//
	// `splashMetrics` is set only when the splash showcase mounts this
	// component. The real dashboard leaves it unset, so the two KPI tiles
	// with no backing API (p95 duration, queue wait) keep their
	// "awaiting metrics endpoint" stub.
	type SplashMetricCard = {
		value: string;
		sub: string;
		delta: string;
		deltaDirection: 'up' | 'down';
		values: number[];
	};
	type SplashMetrics = {
		p95Duration: SplashMetricCard;
		queueWait: SplashMetricCard;
	};
	const {
		data,
		disableHeaderTransition = false,
		splashMetrics
	}: {
		data: PageProps['data'];
		disableHeaderTransition?: boolean;
		splashMetrics?: SplashMetrics;
	} = $props();
	const organization = getOrganizationContext();
	const hessianClient = getHessianClientContext();

	// Check for invitation success message
	onMount(async () => {
		const joinedOrg = page.url.searchParams.get('joined');
		if (joinedOrg) {
			// Invalidate organizations query to pick up the newly joined org
			await data.queryClient.invalidateQueries({
				queryKey: ['get', '/v1/organizations']
			});

			toast.success(`You've joined ${joinedOrg}!`);
			// Remove the query param from URL without reloading
			const url = new URL(page.url);
			url.searchParams.delete('joined');
			goto(url.toString(), { replaceState: true, noScroll: true });
		}
	});

	// ── Real API queries ────────────────────────────────────────────
	const recentRuns = hessianClient.createQuery('get', '/v1/runs', () => ({
		organizationId: organization.id,
		init: {
			params: {
				query: { limit: 50, sort_by: 'createdAt', sort_order: 'desc' }
			}
		}
	}));

	const workers = hessianClient.createQuery('get', '/v1/workers', () => ({
		organizationId: organization.id,
		init: { params: { query: { limit: 50 } } }
	}));

	const queueGroups = hessianClient.createQuery('get', '/v1/queue_groups', () => ({
		organizationId: organization.id,
		init: { params: { query: { limit: 50 } } }
	}));

	const auditLogs = hessianClient.createQuery('get', '/v1/audit', () => ({
		organizationId: organization.id,
		init: { params: { query: { limit: 20 } } }
	}));

	// ── 24h KPI metrics window ──────────────────────────────────────
	// Dedicated query for the KPI sparklines. Filtered to the last
	// 24h so the buckets actually correspond to that window.
	//
	// Caveat: the `/v1/runs` page size is capped at 50, so for very
	// high-volume orgs we see only the most recent 50 runs in the
	// window — earlier buckets will look empty. A proper fix is
	// either cursor pagination here or a dedicated bucketed metrics
	// endpoint (see TODO-API.md).
	const HOURS_WINDOW = 24;
	const SPARK_BUCKETS = 12;
	const windowStartIso = new Date(
		Date.now() - HOURS_WINDOW * 3600_000
	).toISOString();

	const metricsRuns = hessianClient.createQuery('get', '/v1/runs', () => ({
		organizationId: organization.id,
		init: {
			params: {
				query: {
					limit: 50,
					sort_by: 'createdAt',
					sort_order: 'desc',
					'created_at[gte]': windowStartIso
				}
			}
		}
	}));

	function bucketRuns(
		runs: components['schemas']['RunOverview'][],
		hours: number,
		buckets: number
	) {
		const now = Date.now();
		const start = now - hours * 3600_000;
		const bucketMs = (hours * 3600_000) / buckets;
		const counts = new Array<number>(buckets).fill(0);
		const failed = new Array<number>(buckets).fill(0);
		for (const r of runs) {
			const t = Date.parse(r.created_at);
			if (t < start || t > now) continue;
			const idx = Math.min(buckets - 1, Math.floor((t - start) / bucketMs));
			counts[idx]++;
			if (r.status === 'failed') failed[idx]++;
		}
		const failureRates = counts.map((c, i) =>
			c > 0 ? Math.round((failed[i] / c) * 100) : 0
		);
		return { counts, failed, failureRates };
	}

	const metrics24h = $derived.by(() => {
		const runs = metricsRuns.data?.runs ?? [];
		const { counts, failed, failureRates } = bucketRuns(
			runs,
			HOURS_WINDOW,
			SPARK_BUCKETS
		);
		const totalRuns = counts.reduce((a, b) => a + b, 0);
		const totalFailed = failed.reduce((a, b) => a + b, 0);
		const failureRatePct =
			totalRuns > 0 ? Math.round((totalFailed / totalRuns) * 100) : 0;

		// Direction: recent half vs older half of the same window.
		const half = Math.floor(SPARK_BUCKETS / 2);
		const recent = counts.slice(half).reduce((a, b) => a + b, 0);
		const older = counts.slice(0, half).reduce((a, b) => a + b, 0);
		const throughputDelta = recent - older;
		const recentFailed = failed.slice(half).reduce((a, b) => a + b, 0);
		const olderFailed = failed.slice(0, half).reduce((a, b) => a + b, 0);
		const recentRate = recent > 0 ? (recentFailed / recent) * 100 : 0;
		const olderRate = older > 0 ? (olderFailed / older) * 100 : 0;
		const failureDelta = Math.round(recentRate - olderRate);

		return {
			throughput: counts,
			failureRates,
			totalRuns,
			totalFailed,
			failureRatePct,
			throughputDelta,
			failureDelta
		};
	});

	// ── Run status → Chip / Scatter tone mapping ──────────────────
	// Chip's `status` accepts the UI-package Status union; the API's
	// RunStatus uses a different vocabulary (`completed`, `pending`,
	// `cancelled`, `waiting_for_input`). Normalize so the Chip lights up
	// correctly. Anything we don't recognise falls back to `queued`.
	function runStatusToChip(s: components['schemas']['RunStatus']): Status {
		switch (s) {
			case 'completed':
				return 'done';
			case 'running':
				return 'running';
			case 'failed':
				return 'failed';
			case 'cancelled':
				return 'skipped';
			case 'waiting_for_input':
				return 'waiting';
			case 'pending':
			default:
				return 'queued';
		}
	}

	function runStatusToScatterTone(
		s: components['schemas']['RunStatus']
	): ScatterPoint['tone'] {
		switch (s) {
			case 'completed':
				return 'success';
			case 'running':
				return 'info';
			case 'pending':
			case 'waiting_for_input':
				return 'warn';
			case 'failed':
				return 'danger';
			case 'cancelled':
			default:
				return 'neutral';
		}
	}

	// ── Scatter points from real runs ──────────────────────────────
	// Duration vs createdAt. Falls back to "now - createdAt" for runs
	// without a duration_ms (still-running or no finished_at).
	const scatterPoints = $derived.by<ScatterPoint[]>(() => {
		const runs = recentRuns.data?.runs ?? [];
		const now = Date.now();
		return runs.map((r) => {
			const created = Date.parse(r.created_at);
			const duration =
				r.duration_ms ??
				(r.started_at ? Math.max(0, now - Date.parse(r.started_at)) : 0);
			return {
				x: created,
				y: duration,
				tone: runStatusToScatterTone(r.status),
				label: `${r.name} · ${(duration / 1000).toFixed(2)}s · ${r.status}`,
				href: `/runs/${r.id}`,
				meta: { run: r }
			};
		});
	});

	// Constrain the X (time) axis so a single-point series doesn't blow
	// up. The Scatterplot's internal `niceRange` pads `min === max` by
	// `±|min|` — for unix timestamps that means a ~110-year span,
	// producing thousands of dashed gridlines. Always supply an
	// explicit domain padded around the data: ±15 minutes when there's
	// one point, otherwise 10% of the span on each side.
	const scatterXDomain = $derived.by<[number, number] | undefined>(() => {
		if (scatterPoints.length === 0) return undefined;
		let min = scatterPoints[0].x;
		let max = scatterPoints[0].x;
		for (const p of scatterPoints) {
			if (p.x < min) min = p.x;
			if (p.x > max) max = p.x;
		}
		if (min === max) {
			const pad = 15 * 60_000; // 15 minutes
			return [min - pad, max + pad];
		}
		const span = max - min;
		return [min - span * 0.1, max + span * 0.1];
	});

	function fmtScatterTime(ms: number) {
		const d = new Date(ms);
		return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
	}
	function fmtScatterDuration(ms: number) {
		if (!ms) return '0s';
		if (ms < 1000) return `${Math.round(ms)}ms`;
		const s = ms / 1000;
		if (s < 60) return s % 1 === 0 ? `${s}s` : `${s.toFixed(1)}s`;
		return `${(s / 60).toFixed(1)}m`;
	}

	let scatterLastClick = $state<components['schemas']['Run'] | null>(null);

	// ── Stub sparkline values for TODO-API tiles ───────────────────
	// p95 duration and queue wait time aren't queryable as time-bucketed
	// series today (see TODO-API.md). Pass an empty-shaped array so the
	// tile reserves space matching the live tiles without implying data.
	const STUB_SPARK = new Array<number>(SPARK_BUCKETS).fill(0);

	// ── Latest run (DAG tree) ──────────────────────────────────────
	// `recentRuns` is sorted by createdAt desc, so the first entry is the
	// most recent. The rail's "Latest run" card hides entirely when there
	// is no run (fresh accounts). When one exists, fetch its full detail
	// so we can render the entity DAG as a Tree.
	const latestRun = $derived(recentRuns.data?.runs?.[0] ?? null);

	const latestRunDetail = hessianClient.createQuery('get', '/v1/runs/{runId}', () => ({
		organizationId: organization.id,
		init: { params: { path: { runId: latestRun?.id ?? '' } } },
		options: { enabled: !!latestRun?.id }
	}));

	function entityKind(t: components['schemas']['EntityType']): TreeNode['kind'] {
		switch (t) {
			case 'agent':
				return 'agent';
			case 'subflow':
				return 'workflow';
			case 'task':
				return 'tool';
			case 'map':
				return 'folder';
			case 'text':
			default:
				return 'file';
		}
	}

	// Map the API EntityStatus union to the @hessianhq/ui Status tones the
	// StatusGlyph component understands. Unknown values fall back to
	// `queued`.
	function entityStatusToStatus(s: components['schemas']['EntityStatus']): Status {
		switch (s) {
			case 'completed':
				return 'done';
			case 'running':
			case 'initializing':
				return 'running';
			case 'failed':
				return 'failed';
			case 'cancelled':
			case 'skipped':
				return 'skipped';
			case 'waiting_for_input':
				return 'waiting';
			case 'pending':
			case 'queued':
			case 'scheduled':
			default:
				return 'queued';
		}
	}

	const latestRunTree = $derived.by<{
		nodes: TreeNode[];
		statuses: Map<string, Status>;
	}>(() => {
		const entities = latestRunDetail.data?.entities ?? [];
		const edges = latestRunDetail.data?.edges ?? [];
		const statuses = new Map<string, Status>();
		if (entities.length === 0) return { nodes: [], statuses };

		const byId = new Map(entities.map((e) => [e.id, e]));
		const children = new Map<string, string[]>();
		const incoming = new Map<string, number>();
		for (const edge of edges) {
			if (!children.has(edge.sourceId)) children.set(edge.sourceId, []);
			children.get(edge.sourceId)!.push(edge.targetId);
			incoming.set(edge.targetId, (incoming.get(edge.targetId) ?? 0) + 1);
		}

		// Render the DAG as a tree by duplicating any node with multiple
		// incoming edges under each of its parents. This way a diamond
		// (A→C, B→C) shows C under both A and B — faithful to the
		// dependency graph. Synthesized path-based ids keep each rendered
		// occurrence independently expandable. The `ancestors` set guards
		// against cycles defensively (runs should be acyclic).
		function build(
			entityId: string,
			parentPath: string,
			ancestors: Set<string>
		): TreeNode | null {
			if (ancestors.has(entityId)) return null;
			const e = byId.get(entityId);
			if (!e) return null;
			const nodeId = `${parentPath}/${entityId}`;
			const nextAncestors = new Set(ancestors).add(entityId);
			const childNodes = (children.get(entityId) ?? [])
				.map((cid) => build(cid, nodeId, nextAncestors))
				.filter((n): n is TreeNode => !!n);
			const incomingCount = incoming.get(entityId) ?? 0;
			const label = e.name ?? e.entity_id ?? e.id.slice(0, 8);
			statuses.set(nodeId, entityStatusToStatus(e.status));
			return {
				id: nodeId,
				label,
				meta: incomingCount > 1 ? `${e.status} · shared` : e.status,
				kind: entityKind(e.entity_type),
				children: childNodes.length > 0 ? childNodes : undefined
			};
		}

		const roots: TreeNode[] = [];
		const placedIds = new Set<string>();
		for (const e of entities) {
			if ((incoming.get(e.id) ?? 0) === 0) {
				const n = build(e.id, 'root', new Set());
				if (n) {
					roots.push(n);
					placedIds.add(e.id);
				}
			}
		}
		// Cycle survivors / fully-incoming nodes — promote any unreferenced
		// entity to a root so it doesn't disappear from the view.
		for (const e of entities) {
			if (placedIds.has(e.id)) continue;
			const stillMissing = !roots.some((r) => containsEntity(r, e.id));
			if (stillMissing) {
				const n = build(e.id, 'orphan', new Set());
				if (n) roots.push(n);
			}
		}
		return { nodes: roots, statuses };
	});

	function containsEntity(node: TreeNode, entityId: string): boolean {
		// TreeNode ids are `${path}/${entityId}` — the entityId is the last
		// segment of the path. Recurse into children.
		if (node.id.endsWith(`/${entityId}`)) return true;
		return node.children?.some((c) => containsEntity(c, entityId)) ?? false;
	}

	// Expand every node by default so the DAG is visible at a glance —
	// the scroll wrapper caps the visual height.
	let treeExpanded = $state<Set<string>>(new Set());
	$effect(() => {
		const ids = new Set<string>();
		(function collect(ns: TreeNode[]) {
			for (const n of ns) {
				ids.add(n.id);
				if (n.children) collect(n.children);
			}
		})(latestRunTree.nodes);
		treeExpanded = ids;
	});

	// ── Audit / activity feed ──────────────────────────────────────
	function formatAuditAction(log: components['schemas']['AuditLog']) {
		const entity = String(log.entity_type ?? '').replace(/_/g, ' ');
		switch (log.action) {
			case 'create':
				return `Created ${entity}`;
			case 'update':
				return `Updated ${entity}`;
			case 'delete':
				return `Deleted ${entity}`;
			case 'trigger':
				return `Triggered ${entity}`;
			default:
				return `${log.action} ${entity}`;
		}
	}
	function actionTone(action: string): TimelineTone {
		switch (action) {
			case 'create':
				return 'info';
			case 'update':
				return 'warn';
			case 'delete':
				return 'danger';
			case 'trigger':
				return 'success';
			default:
				return 'info';
		}
	}
	function actionGlyph(action: string): string {
		switch (action) {
			case 'create':
				return '+';
			case 'update':
				return '~';
			case 'delete':
				return '×';
			case 'trigger':
				return '⚡';
			default:
				return '•';
		}
	}

	const activityItems = $derived.by<TimelineItem[]>(() => {
		const logs = auditLogs.data?.logs ?? [];
		return logs.slice(0, 5).map((log) => {
			const url = entityUrl(log.entity_type, log.entity_id);
			return {
				id: log.id,
				title: formatAuditAction(log),
				subject: log.entity_id,
				tone: actionTone(log.action),
				glyph: actionGlyph(log.action),
				time: relativeTime(log.created_at),
				avatar: log.user_name ?? log.user_email ?? 'System',
				meta: log.user_name ?? log.user_email ?? 'System',
				href: url ?? undefined
			};
		});
	});

	// ── Helpers ────────────────────────────────────────────────────
	function relativeTime(iso: string) {
		const t = new Date(iso).getTime();
		const diff = Math.max(0, Date.now() - t);
		const m = Math.floor(diff / 60000);
		if (m < 1) return 'just now';
		if (m < 60) return `${m} min ago`;
		const h = Math.floor(m / 60);
		if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
		const d = Math.floor(h / 24);
		return `${d} day${d === 1 ? '' : 's'} ago`;
	}

	const userName = $derived(
		data.user?.user_metadata?.name ??
			data.user?.email?.split('@')[0] ??
			'there'
	);

	// ── Agentic prompt composer ────────────────────────────────────
	// TODO-API: no "/v1/agent/compose" endpoint yet — submissions log to
	// console. Suggestion chips are static seeds for now.
	let agentPrompt = $state('');
	let agentTextarea: HTMLTextAreaElement | undefined = $state();
	const AGENT_MAX_HEIGHT = 180;

	$effect(() => {
		if (!agentTextarea) return;
		void agentPrompt;
		agentTextarea.style.height = '0px';
		agentTextarea.style.height = Math.min(agentTextarea.scrollHeight, AGENT_MAX_HEIGHT) + 'px';
	});

	const AGENT_SUGGESTIONS = [
		{
			text: 'Workflow that triages support tickets to the right team',
			tone: 'workflow',
			iconSvg: `<svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="4" height="4" rx="0.6"/><rect x="10" y="2" width="4" height="4" rx="0.6"/><rect x="6" y="10" width="4" height="4" rx="0.6"/><path d="M4 6v2h8V6M8 8v2"/></svg>`
		},
		{
			text: 'Agent that drafts weekly investor updates from Linear',
			tone: 'agent',
			iconSvg: `<svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="10" height="8" rx="2"/><path d="M8 2v3M5 8.5h.01M11 8.5h.01M6 11h4"/></svg>`
		},
		{
			text: 'On-call rotation that pages whoever owns the failing service',
			tone: 'spark',
			iconSvg: `<svg viewBox="0 0 16 16" width="11" height="11" fill="currentColor"><path d="M9 1 3 9h4l-1 6 6-8H8l1-6z"/></svg>`
		}
	];

	function submitAgentPrompt() {
		if (!agentPrompt.trim()) return;
		// eslint-disable-next-line no-console
		console.log('[dashboard.agent] submit:', agentPrompt);
		agentPrompt = '';
	}
	function onAgentKey(e: KeyboardEvent) {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			submitAgentPrompt();
		}
	}

	// Disabled by default; components are kept imported/wired so they can be
	// flipped back on once the backing APIs land.
	const SHOW_AGENT_COMPOSER = false;
	const SHOW_TOKEN_METER = false;
</script>

<div class="dash-shell">
	<!-- ── Header ────────────────────────────────────────────────── -->
	<!-- `view-transition-name: none` opts this <header> out of the
	     cross-page `header` group when the splash showcase embeds this
	     component as a static preview. Without the override, navigating
	     from `/` (splash) to `/dashboard` would morph the splash's
	     dashboard preview title into the real one — visually it reads
	     like a glitch since both are titled "Overview". -->
	<header
		class="dash-header"
		style:view-transition-name={disableHeaderTransition ? 'none' : 'header'}
	>
		<div>
			<h1 class="dash-title">Overview</h1>
			<p class="dash-greet">
				Welcome back, <span style="color: var(--foreground);">{userName}</span>! Here's a snapshot
				of your system's activity and health.
			</p>
		</div>
	</header>

	<!-- ── Agentic prompt composer ───────────────────────────────── -->
	{#if SHOW_AGENT_COMPOSER}
	<section class="dash-agent" aria-label="Describe a workflow or query past runs">
		<div class="dash-agent-input-row">
			<span class="dash-agent-icon" aria-hidden="true">
				<svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
					<path
						d="M8 0.5l1.5 4.5 4.5 1.5-4.5 1.5L8 12.5 6.5 8 2 6.5 6.5 5 8 0.5zM13 11l0.6 1.9L15.5 13.5l-1.9 0.6L13 16l-0.6-1.9L10.5 13.5l1.9-0.6L13 11z"
					/>
				</svg>
			</span>
			<textarea
				bind:this={agentTextarea}
				bind:value={agentPrompt}
				rows="1"
				placeholder="Describe a workflow or agent in plain English…"
				class="dash-agent-input"
				onkeydown={onAgentKey}
				aria-label="Describe a workflow or query past runs"
			></textarea>
			<div class="dash-agent-top-actions">
				<button
					type="button"
					class="dash-agent-icon-btn"
					aria-label="Voice input"
					title="Voice input"
				>
					<svg
						viewBox="0 0 24 24"
						width="14"
						height="14"
						fill="none"
						stroke="currentColor"
						stroke-width="1.7"
						stroke-linecap="round"
						stroke-linejoin="round"
					>
						<rect x="9" y="3" width="6" height="12" rx="3" />
						<path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
					</svg>
				</button>
				<button
					type="button"
					class="dash-agent-send"
					onclick={submitAgentPrompt}
					disabled={!agentPrompt.trim()}
					aria-label="Send prompt"
					title="Send prompt"
				>
					<svg
						viewBox="0 0 24 24"
						width="14"
						height="14"
						fill="none"
						stroke="currentColor"
						stroke-width="1.7"
						stroke-linecap="round"
						stroke-linejoin="round"
					>
						<path d="M22 2 11 13" />
						<path d="M22 2 15 22l-4-9-9-4 20-7z" />
					</svg>
				</button>
			</div>
		</div>

		<div class="dash-agent-suggestions">
			{#each AGENT_SUGGESTIONS as s (s.text)}
				<button
					type="button"
					class="dash-agent-chip"
					onclick={() => {
						agentPrompt = s.text;
					}}
				>
					<span
						class="dash-agent-chip-icon dash-agent-chip-icon-{s.tone}"
						aria-hidden="true"
					>
						{@html s.iconSvg}
					</span>
					<span>{s.text}</span>
				</button>
			{/each}
		</div>

		<div class="dash-agent-footer">
			<div class="dash-agent-footer-left">
				<button type="button" class="dash-agent-mode">
					<span class="dash-agent-mode-icon" aria-hidden="true">+</span>
					<span>Create</span>
				</button>
				<button type="button" class="dash-agent-mode dash-agent-mode-active">
					<span class="dash-agent-mode-icon" aria-hidden="true">
						<svg
							viewBox="0 0 16 16"
							width="11"
							height="11"
							fill="none"
							stroke="currentColor"
							stroke-width="1.5"
							stroke-linecap="round"
							stroke-linejoin="round"
						>
							<path d="M1 8c1.5 -3 3 -3 4.5 0s3 3 4.5 0 3 -3 4.5 0" />
						</svg>
					</span>
					<span>Ask</span>
				</button>
				<span class="dash-agent-divider" aria-hidden="true"></span>
				<span class="dash-agent-model">claude · sonnet 4.5</span>
			</div>
			<div class="dash-agent-footer-right">
				<span><kbd class="dash-agent-kbd">↵</kbd> to send</span>
				<span class="sep" aria-hidden="true">·</span>
				<span><kbd class="dash-agent-kbd">⇧↵</kbd> for newline</span>
			</div>
		</div>
	</section>
	{/if}

	<!-- ── KPI tiles ─────────────────────────────────────────────── -->
	<section class="dash-tiles">
		<!-- Throughput: real, bucketed from /v1/runs over 24h. -->
		<a href={resolve('/runs')} class="dash-tile-link">
			<MetricCard
				title="throughput · 24h"
				value={metrics24h.totalRuns}
				sub="runs in the last 24h"
				delta={metrics24h.throughputDelta >= 0
					? `+${metrics24h.throughputDelta} vs prior 12h`
					: `${metrics24h.throughputDelta} vs prior 12h`}
				deltaDirection={metrics24h.throughputDelta >= 0 ? 'up' : 'down'}
				values={metrics24h.throughput}
				accentCount={SPARK_BUCKETS / 2}
			>
				{#snippet footer()}
					<span class="dash-tile-foot-row">
						<span>View runs</span>
						<span class="docs-arrow" aria-hidden="true">→</span>
					</span>
				{/snippet}
			</MetricCard>
		</a>

		<!-- Failure rate: real, bucketed from /v1/runs over 24h. -->
		<a href={resolve('/runs')} class="dash-tile-link">
			<MetricCard
				title="failure rate · 24h"
				value={metrics24h.totalRuns > 0 ? `${metrics24h.failureRatePct}%` : '—'}
				sub="{metrics24h.totalFailed} failed of {metrics24h.totalRuns} runs"
				delta={metrics24h.failureDelta >= 0
					? `+${metrics24h.failureDelta} pts`
					: `${metrics24h.failureDelta} pts`}
				deltaDirection={metrics24h.failureDelta > 0 ? 'down' : 'up'}
				sparkTone="bad"
				values={metrics24h.failureRates}
				accentCount={SPARK_BUCKETS / 2}
			>
				{#snippet footer()}
					<span class="dash-tile-foot-row">
						<span>View runs</span>
						<span class="docs-arrow" aria-hidden="true">→</span>
					</span>
				{/snippet}
			</MetricCard>
		</a>

		<!-- TODO-API: p95 run duration is derivable from run.duration_ms,
		     but bucketing past the 50-run page cap needs cursor pagination
		     or a server-side aggregated metrics endpoint. Stubbed for now —
		     the splash showcase passes `splashMetrics` to swap in plausible
		     numbers so the marketing screenshot doesn't read as broken. -->
		<a href={resolve('/runs')} class="dash-tile-link">
			<MetricCard
				title="p95 duration · 24h"
				value={splashMetrics?.p95Duration.value ?? '—'}
				sub={splashMetrics?.p95Duration.sub ?? 'bucketed p95 latency over 24h'}
				delta={splashMetrics?.p95Duration.delta}
				deltaDirection={splashMetrics?.p95Duration.deltaDirection}
				values={splashMetrics?.p95Duration.values ?? STUB_SPARK}
				accentCount={splashMetrics ? SPARK_BUCKETS / 2 : undefined}
			>
				{#snippet footer()}
					<span class="dash-tile-foot-row">
						{#if splashMetrics}
							<span>View runs</span>
						{:else}
							<span class="dash-tile-stub">awaiting metrics endpoint</span>
						{/if}
						<span class="docs-arrow" aria-hidden="true">→</span>
					</span>
				{/snippet}
			</MetricCard>
		</a>

		<!-- TODO-API: queue wait time (task.started_at − task.created_at)
		     is the platform-health signal for a queue-based system, but
		     there's no time-bucketed exposure today. Stubbed for now —
		     see the p95 tile above for the splash-showcase override pattern. -->
		<a href={resolve('/queue_groups')} class="dash-tile-link">
			<MetricCard
				title="queue wait p95 · 24h"
				value={splashMetrics?.queueWait.value ?? '—'}
				sub={splashMetrics?.queueWait.sub ?? 'time from enqueue to worker pickup'}
				delta={splashMetrics?.queueWait.delta}
				deltaDirection={splashMetrics?.queueWait.deltaDirection}
				values={splashMetrics?.queueWait.values ?? STUB_SPARK}
				accentCount={splashMetrics ? SPARK_BUCKETS / 2 : undefined}
			>
				{#snippet footer()}
					<span class="dash-tile-foot-row">
						{#if splashMetrics}
							<span>View queue groups</span>
						{:else}
							<span class="dash-tile-stub">awaiting metrics endpoint</span>
						{/if}
						<span class="docs-arrow" aria-hidden="true">→</span>
					</span>
				{/snippet}
			</MetricCard>
		</a>
	</section>

	<!-- ── Recent Activity (2/3) + Live rail (1/3) ───────────────── -->
	<div class="dash-grid">
		<div class="dash-main">
			<section class="dash-block" id="runs">
				<div class="dash-block-head">
					<h2 class="dash-h2">Recent Activity</h2>
				</div>

				<!-- Workflow Runs scatter -->
				<article class="dash-card">
					<header class="dash-card-head-row">
						<h3 class="dash-card-title">Workflow Runs</h3>
						<a href={resolve('/runs')} class="dash-card-link">View all →</a>
					</header>
					<div class="dash-card-body">
						{#if scatterPoints.length > 0}
							<Scatterplot
								points={scatterPoints}
								xLabel="time · UTC"
								yLabel="duration"
								xFormat={fmtScatterTime}
								yFormat={fmtScatterDuration}
								xTicks={6}
								yTicks={4}
								xDomain={scatterXDomain}
								pointRadius={3}
								height={240}
								yBaseline={0}
								onPointClick={(p) => {
									const r = p.meta?.run as
										| components['schemas']['Run']
										| undefined;
									scatterLastClick = r ?? null;
								}}
							>
								{#snippet tooltip(p: ScatterPoint)}
									{@const r = p.meta?.run as
										| components['schemas']['Run']
										| undefined}
									{#if r}
										<div class="dash-scatter-tip">
											<div class="dash-scatter-tip-name">{r.name}</div>
											<div class="dash-scatter-tip-wf">
												{r.workflow
													.name}
											</div>
											<div class="dash-scatter-tip-grid">
												<span>duration</span>
												<span class="dash-scatter-tip-v"
													>{fmtScatterDuration(
														r.duration_ms ?? 0
													)}</span
												>
												<span>started</span>
												<span class="dash-scatter-tip-v"
													>{fmtScatterTime(Date.parse(r.created_at))}Z</span
												>
												<span>status</span>
												<span
													class="dash-scatter-tip-v dash-scatter-tip-status dash-scatter-tip-status-{r.status}"
													>{r.status}</span
												>
											</div>
											<div class="dash-scatter-tip-cta">
												Click to view <span class="docs-arrow" aria-hidden="true"
													>→</span
												>
											</div>
										</div>
									{/if}
								{/snippet}
							</Scatterplot>
							<div class="dash-scatter-foot">
								<span class="dash-tile-label"
									>{scatterPoints.length} runs · most recent</span
								>
								<span class="dash-scatter-legend">
									<span class="dash-scatter-key dash-scatter-key-success"></span>completed
									<span class="dash-scatter-key dash-scatter-key-info"></span>running
									<span class="dash-scatter-key dash-scatter-key-warn"></span>pending
									<span class="dash-scatter-key dash-scatter-key-danger"></span>failed
									<span class="dash-scatter-key dash-scatter-key-neutral"></span>cancelled
								</span>
							</div>
							{#if scatterLastClick}
								<div class="dash-scatter-clicked">
									<span>opened</span>
									<span class="docs-arrow" aria-hidden="true">→</span>
									<a
										href={resolve(`/runs/${scatterLastClick.id}`)}
										class="dash-scatter-clicked-name"
										>{scatterLastClick.name}</a
									>
								</div>
							{/if}
						{:else if recentRuns.isLoading}
							<div class="dash-card-skeleton" aria-hidden="true"></div>
						{:else}
							<div class="dash-card-empty">
								No workflow runs yet — trigger a workflow to start populating
								this chart.
							</div>
						{/if}
					</div>
				</article>

				<!-- Latest Runs -->
				<article class="dash-card" style="margin-top: 16px;">
					<header class="dash-card-head-row">
						<h3 class="dash-card-title">Latest Runs</h3>
						<a href={resolve('/runs')} class="dash-card-link">View all →</a>
					</header>
					<div class="dash-card-list">
						{#if recentRuns.isLoading && !recentRuns.data}
							{#each Array(5) as _, i (i)}
								<div class="dash-row-skeleton" aria-hidden="true"></div>
							{/each}
						{:else if (recentRuns.data?.runs ?? []).length > 0}
							{#each (recentRuns.data?.runs ?? []).slice(0, 8) as run (run.id)}
								<a href={resolve(`/runs/${run.id}`)} class="dash-row">
									<div class="dash-row-main">
										<span class="dash-row-title">{run.name}</span>
										<span class="dash-row-sub">
											<svg
												width="11"
												height="11"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												stroke-width="1.6"
												stroke-linecap="round"
												stroke-linejoin="round"
												aria-hidden="true"
											>
												<rect x="3" y="3" width="7" height="7" rx="1" />
												<rect x="14" y="3" width="7" height="7" rx="1" />
												<rect x="3" y="14" width="7" height="7" rx="1" />
											</svg>
											{run.workflow.name}
										</span>
									</div>
									<div class="dash-row-right">
										<Chip status={runStatusToChip(run.status)}>{run.status}</Chip>
										<span class="dash-row-time">{relativeTime(run.created_at)}</span>
									</div>
								</a>
							{/each}
						{:else}
							<div class="dash-card-empty">No recent runs.</div>
						{/if}
					</div>
					{#if recentRuns.data?.runs && recentRuns.data.runs.length > 8}
						<footer class="dash-card-foot">
							<a href={resolve('/runs')} class="dash-card-link"
								>View all {recentRuns.data.runs.length} runs →</a
							>
						</footer>
					{/if}
				</article>
			</section>
		</div>
		<!-- /.dash-main -->

		<!-- ── Live-now rail ─────────────────────────────────────── -->
		<aside class="dash-rail" aria-label="Live now">
			<div class="dash-rail-sticky">
				<div class="dash-rail-head">
					<span class="docs-live-dot"></span>
					<span class="docs-eyebrow">Live now</span>
					{#if latestRun}
						<span class="docs-ticker">
							<span class="sep">·</span>
							<span>run.{latestRun.id.slice(0, 6)}</span>
						</span>
					{/if}
				</div>

				<!-- Latest run -->
				{#if latestRun}
					<article class="dash-card">
						<header class="dash-card-head-row">
							<div style="min-width: 0;">
								<h3 class="dash-card-title">Latest run</h3>
								<div class="dash-rail-sub">
									{latestRun.workflow.name}
								</div>
							</div>
							<a href={resolve(`/runs/${latestRun.id}`)} class="dash-card-link">View →</a>
						</header>
						<div class="dash-card-body">
							{#if latestRunDetail.isLoading && !latestRunDetail.data}
								<div class="dash-card-skeleton" aria-hidden="true"></div>
							{:else if latestRunTree.nodes.length > 0}
								<div class="dash-tree-scroll">
									<Tree nodes={latestRunTree.nodes} bind:expanded={treeExpanded}>
										{#snippet row(n: TreeNode)}
											<StatusGlyph
												status={latestRunTree.statuses.get(n.id) ?? 'queued'}
												class="dash-tree-status"
											/>
											<span class="dash-tree-label">{n.label}</span>
											{#if n.meta}
												<span class="dash-tree-meta">{n.meta}</span>
											{/if}
										{/snippet}
									</Tree>
								</div>
							{:else}
								<div class="dash-card-empty">No tasks in this run.</div>
							{/if}
						</div>
					</article>
				{/if}

				<!-- Token quota (mocked) -->
				{#if SHOW_TOKEN_METER}
					<article class="dash-card">
						<header class="dash-card-head-row">
							<h3 class="dash-card-title">Token quota · 24h</h3>
							<a href={resolve('/audit')} class="dash-card-link">Details →</a>
						</header>
						<div class="dash-card-body dash-rail-quota">
							<!-- TODO-API: token-quota / token-meter values are mocked. -->
							<div class="dash-rail-gauge">
								<Gauge
									value={1.42}
									max={2}
									warn={1.6}
									danger={1.9}
									size={140}
									stroke={5}
									label="1.42M"
									sub="of 2.0M"
								/>
							</div>
							<TokenMeter
								used={141200}
								max={200000}
								costUsd={1.32}
								label="claude-opus-4-7 · ctx"
								breakdown={[
									{ label: 'input', tokens: 88000 },
									{ label: 'output', tokens: 41200 },
									{ label: 'cached', tokens: 12000 }
								]}
							/>
						</div>
					</article>
				{/if}

				<!-- Activity Feed (Timeline) -->
				<article class="dash-card">
					<header class="dash-card-head-row">
						<h3 class="dash-card-title">Activity Feed</h3>
						<a href={resolve('/audit')} class="dash-card-link">View all →</a>
					</header>
					<div class="dash-card-body">
						{#if activityItems.length > 0}
							<Timeline items={activityItems} label="Recent activity" />
						{:else if auditLogs.isLoading}
							<div class="dash-card-skeleton" aria-hidden="true"></div>
						{:else}
							<div class="dash-card-empty">No recent activity.</div>
						{/if}
					</div>
				</article>
			</div>
		</aside>
	</div>
	<!-- /.dash-grid -->

	<!-- ── System Status ─────────────────────────────────────────── -->
	<section class="dash-block" id="workers">
		<div class="dash-block-head">
			<h2 class="dash-h2">System Status</h2>
		</div>

		<div class="dash-2col">
			<!-- Worker Health -->
			<article class="dash-card">
				<header class="dash-card-head-row">
					<h3 class="dash-card-title">Worker Health</h3>
					<a href={resolve('/workers')} class="dash-card-link">View all →</a>
				</header>
				<div class="dash-card-list">
					{#if workers.isLoading && !workers.data}
						{#each Array(3) as _, i (i)}
							<div class="dash-row-skeleton" aria-hidden="true"></div>
						{/each}
					{:else if (workers.data?.workers ?? []).length > 0}
						{#each (workers.data?.workers ?? []).slice(0, 5) as worker (worker.name)}
							<a href={resolve(`/workers/${worker.name}`)} class="dash-row">
								<div class="dash-row-main dash-row-main-iconed">
									<StatusGlyph
										status={worker.status === 'active' ? 'running' : 'failed'}
									/>
									<div>
										<div class="dash-row-title">{worker.name}</div>
										<div class="dash-row-sub">{worker.type}</div>
									</div>
								</div>
								<div class="dash-row-right">
									<Chip status={worker.status === 'active' ? 'running' : 'failed'}>
										{worker.status}
									</Chip>
								</div>
							</a>
						{/each}
					{:else}
						<div class="dash-card-empty">No workers found.</div>
					{/if}
				</div>
			</article>

			<!-- Queue Groups -->
			<article class="dash-card" id="queues">
				<header class="dash-card-head-row">
					<h3 class="dash-card-title">Queue Groups</h3>
					<a href={resolve('/queue_groups')} class="dash-card-link">View all →</a>
				</header>
				<div class="dash-card-list">
					{#if queueGroups.isLoading && !queueGroups.data}
						{#each Array(3) as _, i (i)}
							<div class="dash-row-skeleton" aria-hidden="true"></div>
						{/each}
					{:else if (queueGroups.data?.queue_groups ?? []).length > 0}
						{#each queueGroups.data?.queue_groups ?? [] as qg (qg.id)}
							<div class="dash-row">
								<div class="dash-row-main dash-row-main-iconed">
									<span
										class="dash-queue-dot"
										class:dash-queue-dot-default={qg.default}
										aria-hidden="true"
									></span>
									<div>
										<div class="dash-row-title">{qg.name}</div>
										{#if qg.description}
											<div class="dash-row-sub">{qg.description}</div>
										{/if}
									</div>
								</div>
								{#if qg.default}
									<div class="dash-row-right">
										<span class="dash-default-pill">Default</span>
									</div>
								{/if}
							</div>
						{/each}
					{:else}
						<div class="dash-card-empty">No queue groups found.</div>
					{/if}
				</div>
			</article>
		</div>
	</section>
</div>

<style>
	/* ── Shell ──────────────────────────────────────────────────────── */
	.dash-shell {
		margin: 0 auto;
	}
	.dash-header {
		padding-bottom: 24px;
		border-bottom: 1px solid color-mix(in oklab, var(--foreground) 10%, transparent);
	}
	.dash-title {
		margin: 0;
		font-family: var(--font-sans);
		font-weight: 600;
		letter-spacing: -0.028em;
		font-size: 30px;
		line-height: 1.1;
		color: var(--foreground);
	}
	.dash-greet {
		margin: 6px 0 0;
		color: var(--muted-foreground);
		font-size: 14px;
		line-height: 1.55;
	}

	/* ── 2-col grid scoped to Recent Activity + Live rail ─────────────── */
	.dash-grid {
		margin-top: 36px;
		display: grid;
		grid-template-columns: minmax(0, 2fr) minmax(280px, 1fr);
		gap: 24px;
		align-items: flex-start;
	}
	.dash-main {
		min-width: 0;
	}
	.dash-main > .dash-block:first-child {
		margin-top: 0;
	}

	/* ── Agentic prompt composer ────────────────────────────────────── */
	.dash-agent {
		margin-top: 24px;
		padding: 14px 14px 10px;
		background: color-mix(in oklab, var(--foreground) 1.2%, var(--background));
		border: 1px solid color-mix(in oklab, var(--foreground) 8%, transparent);
		border-radius: 8px;
		display: flex;
		flex-direction: column;
		gap: 12px;
		transition: border-color 160ms cubic-bezier(0.4, 0, 0.2, 1);
	}
	.dash-agent:focus-within {
		border-color: color-mix(in oklab, var(--brand-green, oklch(0.7 0.18 145)) 45%, transparent);
	}
	.dash-agent-input-row {
		display: flex;
		align-items: flex-start;
		gap: 10px;
	}
	.dash-agent-icon {
		flex-shrink: 0;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 22px;
		height: 22px;
		margin-top: 6px;
		color: var(--brand-green, oklch(0.7 0.18 145));
		opacity: 0.85;
	}
	.dash-agent-input {
		flex: 1;
		min-width: 0;
		border: none;
		outline: none;
		resize: none;
		background: transparent;
		color: var(--foreground);
		font-family: var(--font-sans);
		font-size: 14.5px;
		line-height: 1.5;
		padding: 6px 0;
		min-height: 28px;
		max-height: 180px;
	}
	.dash-agent-input::placeholder {
		color: var(--muted-foreground);
	}
	.dash-agent-top-actions {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		flex-shrink: 0;
		margin-top: 3px;
	}
	.dash-agent-icon-btn {
		width: 28px;
		height: 28px;
		border: none;
		background: transparent;
		color: var(--muted-foreground);
		border-radius: 4px;
		cursor: pointer;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		transition:
			color 140ms cubic-bezier(0.4, 0, 0.2, 1),
			background 140ms cubic-bezier(0.4, 0, 0.2, 1);
	}
	.dash-agent-icon-btn:hover {
		color: var(--foreground);
		background: color-mix(in oklab, var(--foreground) 5%, transparent);
	}
	.dash-agent-send {
		width: 28px;
		height: 28px;
		border: 1px solid transparent;
		background: color-mix(in oklab, var(--brand-green, oklch(0.7 0.18 145)) 90%, transparent);
		color: oklch(0.12 0 0);
		border-radius: 4px;
		cursor: pointer;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		transition:
			background 140ms cubic-bezier(0.4, 0, 0.2, 1),
			opacity 140ms cubic-bezier(0.4, 0, 0.2, 1);
	}
	.dash-agent-send:hover:not(:disabled) {
		background: var(--brand-green, oklch(0.7 0.18 145));
	}
	.dash-agent-send:disabled {
		opacity: 0.35;
		cursor: not-allowed;
	}
	.dash-agent-suggestions {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
	}
	.dash-agent-chip {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 4px 10px 4px 8px;
		border-radius: 999px;
		border: 1px solid color-mix(in oklab, var(--foreground) 10%, transparent);
		background: transparent;
		color: var(--muted-foreground);
		font-family: var(--font-mono);
		font-size: 11px;
		letter-spacing: 0.01em;
		cursor: pointer;
		transition:
			color 140ms cubic-bezier(0.4, 0, 0.2, 1),
			border-color 140ms cubic-bezier(0.4, 0, 0.2, 1),
			background 140ms cubic-bezier(0.4, 0, 0.2, 1);
	}
	.dash-agent-chip:hover {
		color: var(--foreground);
		border-color: color-mix(in oklab, var(--foreground) 22%, transparent);
		background: color-mix(in oklab, var(--foreground) 3%, transparent);
	}
	.dash-agent-chip-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		color: color-mix(in oklab, var(--foreground) 50%, transparent);
		transition: color 140ms cubic-bezier(0.4, 0, 0.2, 1);
	}
	.dash-agent-chip:hover .dash-agent-chip-icon {
		color: var(--brand-green, oklch(0.7 0.18 145));
	}
	.dash-agent-chip-icon-workflow,
	.dash-agent-chip-icon-agent,
	.dash-agent-chip-icon-spark {
		color: inherit;
	}
	.dash-agent-footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
		padding-top: 8px;
		border-top: 1px dashed color-mix(in oklab, var(--foreground) 8%, transparent);
		flex-wrap: wrap;
	}
	.dash-agent-footer-left {
		display: inline-flex;
		align-items: center;
		gap: 4px;
	}
	.dash-agent-mode {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		height: 22px;
		padding: 0 8px;
		border: none;
		background: transparent;
		color: var(--muted-foreground);
		border-radius: 3px;
		cursor: pointer;
		font-family: var(--font-mono);
		font-size: 10.5px;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		transition:
			color 140ms cubic-bezier(0.4, 0, 0.2, 1),
			background 140ms cubic-bezier(0.4, 0, 0.2, 1);
	}
	.dash-agent-mode:hover {
		color: var(--foreground);
		background: color-mix(in oklab, var(--foreground) 4%, transparent);
	}
	.dash-agent-mode-active {
		color: var(--foreground);
		background: color-mix(in oklab, var(--foreground) 6%, transparent);
	}
	.dash-agent-mode-active .dash-agent-mode-icon {
		color: var(--brand-green, oklch(0.7 0.18 145));
	}
	.dash-agent-mode-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 11px;
		color: var(--muted-foreground);
		font-family: var(--font-mono);
		font-size: 11px;
		line-height: 1;
	}
	.dash-agent-divider {
		display: inline-block;
		width: 1px;
		height: 12px;
		background: color-mix(in oklab, var(--foreground) 12%, transparent);
		margin: 0 6px;
	}
	.dash-agent-model {
		font-family: var(--font-mono);
		font-size: 10.5px;
		color: var(--muted-foreground);
		letter-spacing: 0.04em;
	}
	.dash-agent-footer-right {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		font-family: var(--font-mono);
		font-size: 10px;
		color: var(--muted-foreground);
		letter-spacing: 0.04em;
		text-transform: lowercase;
	}
	.dash-agent-footer-right .sep {
		color: color-mix(in oklab, var(--foreground) 18%, transparent);
	}
	.dash-agent-kbd {
		font-family: var(--font-mono);
		font-size: 10px;
		padding: 0 4px;
		height: 15px;
		display: inline-flex;
		align-items: center;
		border-radius: 3px;
		border: 1px solid color-mix(in oklab, var(--foreground) 12%, transparent);
		background: color-mix(in oklab, var(--foreground) 3%, transparent);
		color: var(--foreground);
		margin-right: 3px;
	}

	/* ── KPI tiles ───────────────────────────────────────────────────── */
	.dash-tiles {
		margin-top: 24px;
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 16px;
	}
	.dash-tile-link {
		display: block;
		text-decoration: none;
		color: inherit;
		border-radius: 6px;
		transition:
			transform 160ms var(--ease-in-out-cubic),
			box-shadow 160ms var(--ease-in-out-cubic);
	}
	.dash-tile-link:hover {
		transform: translateY(-1px);
		box-shadow: 0 8px 24px -16px oklch(0 0 0 / 0.4);
	}
	.dash-tile-link :global(.hk-card) {
		height: 100%;
		transition: border-color 160ms var(--ease-in-out-cubic);
	}
	.dash-tile-link:hover :global(.hk-card) {
		border-color: color-mix(in oklab, var(--foreground) 22%, transparent);
	}
	.dash-tile-foot-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
	}
	.dash-tile-link:hover .dash-tile-foot-row {
		color: var(--foreground);
	}
	.dash-tile-stub {
		font-style: italic;
		opacity: 0.7;
	}

	/* ── Section heads ─────────────────────────────────────────────── */
	.dash-block {
		margin-top: 36px;
	}
	.dash-block-head {
		display: flex;
		align-items: center;
		gap: 12px;
		margin-bottom: 20px;
	}
	.dash-h2 {
		margin: 0;
		font-family: var(--font-sans);
		font-size: 19px;
		font-weight: 600;
		letter-spacing: -0.018em;
		color: var(--foreground);
	}
	.dash-2col {
		margin-top: 16px;
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 16px;
	}

	/* ── Card primitive ────────────────────────────────────────────── */
	.dash-card {
		display: flex;
		flex-direction: column;
		background: color-mix(in oklab, var(--foreground) 1.2%, var(--background));
		border: 1px solid color-mix(in oklab, var(--foreground) 8%, transparent);
		border-radius: 8px;
		overflow: hidden;
	}
	.dash-card-head-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 16px 16px 10px;
	}
	.dash-card-title {
		margin: 0;
		font-family: var(--font-sans);
		font-size: 14.5px;
		font-weight: 600;
		color: var(--foreground);
	}
	.dash-card-link {
		font-family: var(--font-mono);
		font-size: 11px;
		color: var(--muted-foreground);
		text-decoration: none;
		transition: color 160ms var(--ease-in-out-cubic);
	}
	.dash-card-link:hover {
		color: var(--foreground);
	}
	.dash-card-body {
		padding: 0 16px 16px;
	}
	.dash-card-list {
		padding: 0 12px 12px;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}
	.dash-card-foot {
		margin-top: auto;
		background: color-mix(in oklab, var(--foreground) 3%, transparent);
		border-top: 1px solid color-mix(in oklab, var(--foreground) 8%, transparent);
		padding: 10px 16px;
		text-align: center;
	}
	.dash-card-empty {
		padding: 24px 4px;
		font-family: var(--font-mono);
		font-size: 11.5px;
		color: var(--muted-foreground);
		text-align: center;
		letter-spacing: 0.02em;
	}
	.dash-card-skeleton {
		height: 160px;
		border-radius: 4px;
		background: color-mix(in oklab, var(--foreground) 4%, transparent);
	}
	.dash-row-skeleton {
		height: 44px;
		border-radius: 4px;
		background: color-mix(in oklab, var(--foreground) 4%, transparent);
	}

	/* ── Scatter footer / legend / tooltip ─────────────────────────── */
	.dash-scatter-foot {
		margin-top: 14px;
		padding-top: 12px;
		border-top: 1px dashed color-mix(in oklab, var(--foreground) 10%, transparent);
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		flex-wrap: wrap;
	}
	.dash-scatter-legend {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		font-family: var(--font-mono);
		font-size: 8.5px;
		color: var(--muted-foreground);
		letter-spacing: 0.04em;
		text-transform: uppercase;
	}
	.dash-scatter-key {
		display: inline-block;
		width: 5px;
		height: 5px;
		border-radius: 9999px;
		margin-right: 3px;
		vertical-align: middle;
	}
	.dash-scatter-key-success {
		background: oklch(70.5% 0.18 153);
	}
	.dash-scatter-key-info {
		background: oklch(0.7 0.18 220);
	}
	.dash-scatter-key-warn {
		background: oklch(0.72 0.14 60);
	}
	.dash-scatter-key-danger {
		background: oklch(0.7 0.18 25);
	}
	.dash-scatter-key-neutral {
		background: color-mix(in oklab, var(--foreground) 45%, transparent);
	}
	.dash-scatter-clicked {
		margin-top: 10px;
		display: inline-flex;
		align-items: center;
		gap: 8px;
		font-family: var(--font-mono);
		font-size: 11px;
		color: var(--muted-foreground);
	}
	.dash-scatter-clicked-name {
		color: var(--foreground);
		text-decoration: none;
	}
	.dash-scatter-clicked-name:hover {
		text-decoration: underline;
	}
	.dash-scatter-tip {
		display: flex;
		flex-direction: column;
		gap: 6px;
		min-width: 200px;
	}
	.dash-scatter-tip-name {
		font-family: var(--font-mono);
		font-size: 11.5px;
		color: var(--foreground);
		letter-spacing: 0.02em;
	}
	.dash-scatter-tip-wf {
		font-family: var(--font-mono);
		font-size: 10.5px;
		color: var(--muted-foreground);
		letter-spacing: 0.02em;
	}
	.dash-scatter-tip-grid {
		margin-top: 4px;
		display: grid;
		grid-template-columns: auto 1fr;
		gap: 4px 14px;
		font-family: var(--font-mono);
		font-size: 10.5px;
		color: var(--muted-foreground);
		letter-spacing: 0.02em;
	}
	.dash-scatter-tip-v {
		color: var(--foreground);
		text-align: right;
	}
	.dash-scatter-tip-status {
		text-transform: uppercase;
		letter-spacing: 0.08em;
	}
	.dash-scatter-tip-cta {
		margin-top: 6px;
		padding-top: 8px;
		border-top: 1px dashed color-mix(in oklab, var(--foreground) 14%, transparent);
		font-family: var(--font-mono);
		font-size: 10.5px;
		color: var(--muted-foreground);
		letter-spacing: 0.04em;
		display: flex;
		align-items: center;
		gap: 4px;
	}
	.dash-scatter-tip-status-completed {
		color: oklch(70.5% 0.18 153);
	}
	.dash-scatter-tip-status-running {
		color: oklch(0.7 0.18 220);
	}
	.dash-scatter-tip-status-pending,
	.dash-scatter-tip-status-waiting_for_input {
		color: oklch(0.72 0.14 60);
	}
	.dash-scatter-tip-status-failed {
		color: oklch(0.7 0.18 25);
	}
	.dash-scatter-tip-status-cancelled {
		color: color-mix(in oklab, var(--foreground) 45%, transparent);
	}

	/* ── Row pattern (latest runs · workers · queues) ──────────────── */
	.dash-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 14px;
		padding: 10px 12px;
		border-radius: 6px;
		background: color-mix(in oklab, var(--foreground) 1.6%, transparent);
		border: 1px solid transparent;
		text-decoration: none;
		color: inherit;
		transition:
			background 160ms var(--ease-in-out-cubic),
			border-color 160ms var(--ease-in-out-cubic);
	}
	.dash-row:hover {
		background: color-mix(in oklab, var(--foreground) 4%, transparent);
		border-color: color-mix(in oklab, var(--foreground) 10%, transparent);
	}
	.dash-row-main {
		min-width: 0;
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 2px;
	}
	.dash-row-main-iconed {
		flex-direction: row;
		align-items: center;
		gap: 12px;
	}
	.dash-row-main-iconed > div {
		min-width: 0;
	}
	.dash-row-title {
		font-family: var(--font-sans);
		font-size: 13.5px;
		color: var(--foreground);
		font-weight: 500;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	.dash-row-sub {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		font-family: var(--font-mono);
		font-size: 11px;
		color: var(--muted-foreground);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	.dash-row-right {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: 6px;
		flex-shrink: 0;
	}
	.dash-row-time {
		font-family: var(--font-mono);
		font-size: 10.5px;
		color: var(--muted-foreground);
		letter-spacing: 0.02em;
	}

	/* ── Queue dot ─────────────────────────────────────────────────── */
	.dash-queue-dot {
		width: 10px;
		height: 10px;
		border-radius: 999px;
		background: color-mix(in oklab, var(--foreground) 25%, transparent);
		flex-shrink: 0;
	}
	.dash-queue-dot-default {
		background: oklch(0.7 0.18 220);
		box-shadow: 0 0 0 3px color-mix(in oklab, oklch(0.7 0.18 220) 18%, transparent);
	}
	.dash-default-pill {
		font-family: var(--font-mono);
		font-size: 10px;
		font-weight: 600;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: oklch(0.7 0.18 220);
		background: color-mix(in oklab, oklch(0.7 0.18 220) 12%, transparent);
		border-radius: 999px;
		padding: 3px 8px;
	}

	/* ── Live-now rail ─────────────────────────────────────────────── */
	.dash-rail {
		min-width: 0;
	}
	.dash-rail-sticky {
		position: sticky;
		top: 76px;
		display: flex;
		flex-direction: column;
		gap: 16px;
	}
	.dash-rail-head {
		display: inline-flex;
		align-items: center;
		gap: 10px;
		padding: 0 4px 4px;
	}
	.dash-rail-sub {
		margin-top: 4px;
		font-family: var(--font-mono);
		font-size: 11.5px;
		color: var(--muted-foreground);
		letter-spacing: 0.02em;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	.dash-tree-scroll {
		max-height: 280px;
		overflow-y: auto;
		margin-top: 4px;
		padding: 6px 4px;
		border: 1px solid color-mix(in oklab, var(--foreground) 8%, transparent);
		border-radius: 4px;
		background: color-mix(in oklab, var(--foreground) 1%, transparent);
	}
	/* Row content rendered through Tree's `row` snippet. Sits to the
	 * right of Tree's built-in caret + kind glyph. */
	.dash-tree-scroll :global(.dash-tree-status) {
		margin-left: 2px;
	}
	.dash-tree-scroll :global(.dash-tree-label) {
		flex: 1;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		letter-spacing: 0.005em;
	}
	.dash-tree-scroll :global(.dash-tree-meta) {
		font-family: var(--font-mono);
		font-size: 10px;
		letter-spacing: 0.04em;
		text-transform: uppercase;
		color: var(--muted-foreground);
		padding-left: 6px;
		flex-shrink: 0;
	}
	.dash-rail-quota {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}
	.dash-rail-gauge {
		display: flex;
		justify-content: center;
	}

	/* ── Responsive ────────────────────────────────────────────────── */
	@media (max-width: 1100px) {
		.dash-grid {
			grid-template-columns: 1fr;
		}
		.dash-rail-sticky {
			position: static;
		}
		.dash-tiles {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
	@media (max-width: 720px) {
		.dash-tiles {
			grid-template-columns: 1fr;
		}
		.dash-2col {
			grid-template-columns: 1fr;
		}
	}
</style>
